import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from "@env";
import Swal from 'sweetalert2';
import { finalize } from 'rxjs';

export type TrackingSystem = 'globex' | 'carrier';
export type TrackingDisplay = 'embedded' | 'floating';

@Component({
  selector: 'app-tracking-request',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './tracking-request.component.html',
  styleUrls: ['./tracking-request.component.scss']
})
export class TrackingRequestComponent {
  @Input() display: TrackingDisplay = 'embedded';
  @Input() defaultSystem: TrackingSystem = 'globex';
  @Output() trackingRequested = new EventEmitter<{ system: TrackingSystem; trackingNumber: string }>();

  constructor(private translate: TranslateService, private http: HttpClient, private elementRef: ElementRef<HTMLElement>) {}

  isOpen = false;
  selectedSystem: TrackingSystem = 'globex';
  trackingNumber = '';
  isTracking = false;

  ngOnInit(): void {
    this.selectedSystem = this.defaultSystem;
  }

  selectSystem(system: TrackingSystem): void {
    if (this.selectedSystem === system) return;
    this.selectedSystem = system;
    this.trackingNumber = '';
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.display === 'floating' && this.isOpen) {
      requestAnimationFrame(() => {
        const input = this.elementRef.nativeElement.querySelector<HTMLInputElement>('#tracking-request-number');
        input?.focus({ preventScroll: true });
      });
    }
  }

  close(): void {
    this.isOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.display === 'floating') this.close();
  }

  trackShipment(): void {
    const trackingNumber = this.trackingNumber.trim();
    if (!trackingNumber || this.isTracking) return;

    if (this.selectedSystem === 'globex') {
      const lang = this.translate.currentLang?.toLowerCase() === 'fr' ? 'F' : 'E';
      const url = `https://cloud.globexcourier.com/CCWeb4/Tracking/TrackingInfo?OrderNo=${encodeURIComponent(trackingNumber)}&Lang=${lang}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    this.getServiceTrackingURL(trackingNumber);
  }

  private getServiceTrackingURL(trackingNumber: string): void {
      const lang = this.translate.currentLang?.toLowerCase().startsWith('fr') ? 'F' : 'E';
      const params = new HttpParams().set('TrackingNumber', trackingNumber).set('Lang', lang);

      const showTrackingWarning = (title: string, message: string): void => {
          Swal.fire({
              scrollbarPadding: false,
              icon: 'warning',
              title: title,
              text: message,
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                  popup: 'tracking-swal-popup',
                  icon: 'tracking-swal-icon',
                  title: 'tracking-swal-title',
                  htmlContainer: 'tracking-swal-message',
                  confirmButton: 'tracking-swal-confirm'
              }
          });
      };

      this.isTracking = true;

      this.http.get<{ TrackingURL: string }>(environment.CSDAEndPoint + '/CSDA/GetServiceTrackingURL', { params }).pipe(finalize(() => this.isTracking = false)).subscribe({
          next: (response) => {
              if (response?.TrackingURL) {
                  window.open(response.TrackingURL, '_blank', 'noopener,noreferrer');
              } else {
                  showTrackingWarning(lang === 'F' ? 'Suivi indisponible' : 'Tracking unavailable', lang === 'F' ? 'Aucune URL de suivi disponible pour ce numéro.' : 'No tracking URL is available for this number.');
              }
          },
          error: (error) => {
              showTrackingWarning(lang === 'F' ? 'Suivi introuvable' : 'Tracking not found', error?.error?.message || error?.error?.Message || (lang === 'F' ? 'Impossible de retrouver les informations de suivi.' : 'Unable to retrieve tracking information.'));
          }
      });
  }

}
